import * as THREE from 'three';
import type { ColliderConfig } from '@datum-sdk/scene-format';

const scratchMatrix = new THREE.Matrix4();
const scratchPoint = new THREE.Vector3();
const scratchPosition = new THREE.Vector3();
const scratchQuaternion = new THREE.Quaternion();
const scratchScale = new THREE.Vector3();

/**
 * Point-in-collider for the SDK's procedural shapes, in their unit form before
 * the collider transform: box ±0.5 on every axis, cylinder radius 0.5 / height 1
 * (Y axis), sphere radius 0.5. Any other kind (plane, mesh, voxel) is reported
 * as containing the point, so it never counts as a cage here.
 */
const containsPoint = (collider: ColliderConfig, point: readonly number[]): boolean => {
  const { position = [0, 0, 0], quaternion = [0, 0, 0, 1], scale = 1 } = collider.transform ?? {};
  scratchPosition.fromArray(position);
  scratchQuaternion.fromArray(quaternion);
  if (typeof scale === 'number') scratchScale.setScalar(scale);
  else scratchScale.fromArray(scale);
  scratchMatrix.compose(scratchPosition, scratchQuaternion, scratchScale).invert();
  const local = scratchPoint.fromArray(point).applyMatrix4(scratchMatrix);
  switch (collider.shape) {
    case 'box':
      return Math.abs(local.x) <= 0.5 && Math.abs(local.y) <= 0.5 && Math.abs(local.z) <= 0.5;
    case 'cylinder':
      return local.x * local.x + local.z * local.z <= 0.25 && Math.abs(local.y) <= 0.5;
    case 'sphere':
      return local.lengthSq() <= 0.25;
    default:
      return true;
  }
};

/**
 * True when a world point lies outside one of the containment colliders — the
 * camera cage. The SDK dims a POI marker whenever the camera→POI ray crosses
 * ANY collider that does not contain the POI, cages included, and has no
 * per-collider opt-out like legacy's `blocksLineOfSight: false` (CR POI5) — so
 * a POI outside the cage would stay dimmed from everywhere the camera can be.
 */
export const isOutsideContainment = (
  point: readonly number[],
  colliders: readonly ColliderConfig[],
): boolean =>
  colliders.some((collider) => collider.role === 'containment' && !containsPoint(collider, point));
